import { Button, MessageBar, MessageBarBody, Spinner, makeStyles } from '@fluentui/react-components';
import { Add16Regular } from '@fluentui/react-icons';
import { useEffect, useMemo, useState } from 'react';
import type { Device, Network, PolicyVersion, SiteHost } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { useMe } from '../auth/MeProvider';
import { NoAccess } from '../components/NoAccess';
import { PageAside } from '../components/PageAside';
import { PageHeader } from '../components/PageHeader';
import { useShell } from '../lib/shell';
import { errorText } from '../lib/useRelay';
import { vt } from '../theme';
import { AddSiteDrawer } from './sites/AddSiteDrawer';
import { HardwarePath } from './sites/HardwarePath';
import { reachability, sitesOf } from './sites/model';
import { SiteCard } from './sites/SiteCard';
import { SiteHosts } from './sites/SiteHosts';

const useStyles = makeStyles({
  site: { display: 'flex', flexDirection: 'column', rowGap: '28px' },
  empty: { margin: 0, color: vt.fg3 },
});

/** One site's card and hosts; the hosts are re-read whenever its approved routes change. */
function SiteSection({ site, sites, policy, now }: { site: Device; sites: Device[]; policy: PolicyVersion | null; now: number }) {
  const s = useStyles();
  const { api } = useAuth();
  const { reloadDevices } = useShell();
  const [hosts, setHosts] = useState<{ key: string; list: SiteHost[] | null; error: string | null } | null>(null);
  const key = `${site.id} ${site.routes.join()}`;

  useEffect(() => {
    let live = true;
    api
      .getSiteHosts(site.id)
      .then((list) => live && setHosts({ key, list, error: null }))
      .catch((e: unknown) => live && setHosts({ key, list: null, error: errorText(e) }));
    return () => {
      live = false;
    };
  }, [api, site.id, key]);

  const current = hosts?.key === key ? hosts : null;
  const reach = useMemo(() => (policy ? reachability(site, policy) : null), [site, policy]);
  return (
    <div className={s.site}>
      <SiteCard site={site} hosts={current?.list ?? null} allSites={sites} now={now} onChanged={reloadDevices} />
      <SiteHosts site={site} hosts={current?.list ?? null} error={current?.error ?? null} reach={reach} now={now} />
    </div>
  );
}

function SitesPage() {
  const s = useStyles();
  const { api } = useAuth();
  const { devices, devicesError, reloadDevices } = useShell();
  const [policy, setPolicy] = useState<PolicyVersion | null>(null);
  const [networks, setNetworks] = useState<Network[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [now] = useState(() => Date.now());

  useEffect(() => {
    let live = true;
    Promise.all([api.getPolicy(), api.listNetworks()])
      .then(([p, n]) => {
        if (!live) return;
        setPolicy(p);
        setNetworks(n.networks);
      })
      .catch((e: unknown) => live && setLoadError(errorText(e)));
    return () => {
      live = false;
    };
  }, [api]);

  const sites = useMemo(() => sitesOf(devices ?? []), [devices]);

  const claimed = async (d: Device) => {
    setAdding(false);
    setNotice(
      `Claimed ${d.name} at ${d.overlayIp}. It joins on its next poll (within 15 s while the code is fresh, else 5 min).`,
    );
    await reloadDevices();
  };

  return (
    <>
      <PageHeader
        title="Sites"
        actions={
          <Button appearance="primary" shape="circular" size="small" icon={<Add16Regular />} onClick={() => setAdding(true)}>
            Add site
          </Button>
        }
      />
      {notice && (
        <MessageBar intent="success">
          <MessageBarBody>{notice}</MessageBarBody>
        </MessageBar>
      )}
      {(devicesError || loadError) && (
        <MessageBar intent="error">
          <MessageBarBody>{devicesError ?? loadError}</MessageBarBody>
        </MessageBar>
      )}
      {!devices && !devicesError && <Spinner label="Loading sites" />}
      {devices && sites.length === 0 && (
        <p className={s.empty}>
          No site yet. Install the vipper-site package on an OpenWRT router, power it on, then Add site and type the
          claim code it shows.
        </p>
      )}
      {sites.map((site) => (
        <SiteSection key={site.id} site={site} sites={sites} policy={policy} now={now} />
      ))}
      <HardwarePath />

      {adding && (
        <PageAside label="Add a site">
          <AddSiteDrawer sites={sites} networks={networks} onClose={() => setAdding(false)} onClaimed={claimed} />
        </PageAside>
      )}
    </>
  );
}

/** Mockup 08: OpenWRT routers carrying LAN routes, the hosts behind them, and claim-code onboarding. Admin only. */
export function Sites() {
  const { me, loading, error, isAdmin } = useMe();
  return (
    <>
      {loading && <Spinner label="Loading your permissions" />}
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {me && !isAdmin && (
        <>
          <PageHeader title="Sites" />
          <NoAccess />
        </>
      )}
      {me && isAdmin && <SitesPage />}
    </>
  );
}
